import * as Sentry from "@sentry/react";
import {
  hasCookieConsent,
  onCookieConsentChange,
  type CookieConsentPreferences,
} from "./cookieConsent";

let sentryInitialized = false;

export const isPrivateCapabilityPath = (pathname: string): boolean => {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname || "/";
  return normalized === "/feedback" || normalized === "/recover";
};

export const applySentryConsentState = (
  analyticsConsented: boolean,
  options?: {
    dsn?: string;
    mode?: string;
    pathname?: string;
  },
): boolean => {
  const dsn = options?.dsn ?? import.meta.env.VITE_SENTRY_DSN;
  const mode = options?.mode ?? import.meta.env.MODE;
  const pathname =
    options?.pathname ??
    (typeof window !== "undefined" ? window.location.pathname : "/");

  if (!dsn || isPrivateCapabilityPath(pathname)) {
    return false;
  }

  if (!analyticsConsented) {
    const client = Sentry.getClient();
    if (client) {
      client.getOptions().enabled = false;
    }
    return false;
  }

  const existingClient = Sentry.getClient();
  if (sentryInitialized && existingClient) {
    existingClient.getOptions().enabled = true;
    return true;
  }

  Sentry.init({
    dsn,
    environment: mode,
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
    tracesSampleRate: mode === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    beforeSend(event, hint) {
      if (!hasCookieConsent("analytics")) {
        return null;
      }
      // Drop noisy browser extension / network errors
      const msg =
        hint.originalException instanceof Error
          ? hint.originalException.message
          : "";
      if (
        msg.includes("ResizeObserver loop") ||
        msg.includes("Non-Error promise rejection captured")
      ) {
        return null;
      }
      // Diagnostics must not become a second customer-data store. Strip user,
      // request and breadcrumb payloads; keep stack frames and low-cardinality
      // technical fields required to diagnose the release.
      event.user = undefined;
      event.request = undefined;
      event.extra = undefined;
      event.breadcrumbs = event.breadcrumbs?.map((breadcrumb) => ({
        ...breadcrumb,
        message: undefined,
        data: undefined,
      }));
      return event;
    },
  });

  sentryInitialized = true;
  return true;
};

export const setupConsentAwareSentry = (): (() => void) => {
  applySentryConsentState(hasCookieConsent("analytics"));
  return onCookieConsentChange((prefs: CookieConsentPreferences) => {
    applySentryConsentState(prefs.analytics);
  });
};
